<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="400">
        <v-form v-model="formValid" class="mb-3" validate-on="input lazy" @submit.prevent="storeRecipient">
            <v-card>
                <v-card-title id="heading" class="d-flex justify-space-between">
                    <h4>Add New Recipient</h4>
                    <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
                </v-card-title>

                <v-container>
                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                             density="compact" variant="tonal">
                        <p v-for="error in errors">{{ error }}</p>
                    </v-alert>

                    <v-radio-group v-model="recipientKind" color="primary" inline hide-details
                                   label="Who should we notify?" class="mb-2">
                        <v-radio label="Circle member" value="circle"></v-radio>
                        <v-radio label="Email / SMS" value="external"></v-radio>
                    </v-radio-group>

                    <!-- Circle member: pick an in-app recipient from the circle -->
                    <template v-if="recipientKind === 'circle'">
                        <v-select v-model="selectedUserId" :items="availableUsers" item-title="name"
                                  item-value="id" :loading="loadingUsers" :rules="[vRules.required]"
                                  density="compact" label="Choose a circle member"
                                  prepend-inner-icon="mdi-account"
                                  :no-data-text="loadingUsers ? 'Loading...' : 'Everyone in your circle is already a recipient'"/>
                    </template>

                    <!-- External: an email address or phone number -->
                    <template v-else>
                        <v-radio-group v-model="thirdPartyType" color="primary" inline hide-details
                                       @update:modelValue="() => { formData.recipient = null; }">
                            <v-radio label="Email" value="email"></v-radio>
                            <v-radio label="SMS" value="sms"></v-radio>
                        </v-radio-group>
                        <v-text-field v-model="formData.recipient"
                                      :rules="[vRules.required, thirdPartyType === 'email' ? vRules.email : vRules.numeric]"
                                      :type="thirdPartyType === 'email' ? 'email' : 'number'" density="compact"
                                      label="Recipient"/>
                    </template>
                </v-container>

                <v-card-actions class="px-4 d-flex justify-end">
                    <v-btn size="small" type="button" @click="$emit('update:modelValue',false)">
                        Close
                    </v-btn>
                    <v-btn :disabled="!formValid || saving" :loading="saving" color="primary" size="small" type="submit">
                        Save
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-form>
    </v-dialog>
</template>

<script setup>
import {ref, reactive, computed, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {useAuthStore} from "@/stores/authStore";

const errors = ref([]);
const recipientKind = ref('circle');   // 'circle' (in-app user) | 'external' (email/sms)
const thirdPartyType = ref('email');
const selectedUserId = ref(null);
const circleUsers = ref([]);
const loadingUsers = ref(false);
const formValid = ref(false);
const saving = ref(false);
const snackbar = useSnackbarStore();
const authStore = useAuthStore();
const props = defineProps({
    group: Object,
    modelValue: Boolean,
    // user_ids already in the group, so we don't offer them twice.
    existingUserIds: {type: Array, default: () => []},
});
const dialogSwitch = ref(props.modelValue);
const formData = reactive({
    recipient: null,
});
const emits = defineEmits(['update:modelValue', 'addRecipient']);

// Circle members not already a recipient, labelled for the picker.
const availableUsers = computed(() =>
    circleUsers.value
        .filter((u) => !props.existingUserIds.includes(u.id))
        .map((u) => ({id: u.id, name: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email || `User ${u.id}`})));

onMounted(() => {
    const companyId = authStore.user?.company?.id;
    if (!companyId) {
        recipientKind.value = 'external';
        return;
    }
    loadingUsers.value = true;
    // include_self=1 so the recipient picker can include the signed-in user.
    send('get', `companies/${companyId}/users?list=1&include_self=1`)
        .then((response) => {
            circleUsers.value = response.data?.data?.users ?? [];
        })
        .catch(() => { /* picker stays empty; we fall back to external below */ })
        .finally(() => {
            loadingUsers.value = false;
            // If every circle member is already a recipient (the common case, since
            // a new alert seeds them all) or the fetch failed, start on the external
            // form so the dialog isn't a dead-end.
            if (availableUsers.value.length === 0) recipientKind.value = 'external';
        });
});

const storeRecipient = () => {
    errors.value = [];
    const isCircle = recipientKind.value === 'circle';
    // The backend forces app_enabled off when a recipient (email/sms) is set, and
    // keeps it on for a circle user (recipient null).
    const payload = isCircle
        ? {
            type: 0,
            user_id: selectedUserId.value,
            recipient: null,
            group_id: props.group.id,
            app_enabled: true,
            sms_enabled: false,
            email_enabled: false,
        }
        : {
            type: 1,
            user_id: null,
            recipient: formData.recipient,
            group_id: props.group.id,
            app_enabled: false,
            sms_enabled: thirdPartyType.value === 'sms',
            email_enabled: thirdPartyType.value === 'email',
        };

    saving.value = true;
    send('post', `monitoring/notification-group-members`, payload)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addRecipient', response.data.data.member);
                emits('update:modelValue', false);
            }
        })
        .catch((errorResponse) => {
            if (errorResponse.response?.data?.errors) {
                const validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => errors.value.push(validationErrors[key][0]));
            } else if (errorResponse.response?.data?.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading')?.scrollIntoView({behavior: 'smooth'});
        })
        .finally(() => saving.value = false);
};
</script>
